import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Menu from '../components/Menu';
import FeaturedProducts from '../components/FeaturedProducts';
import BestSellers from '../components/BestSellers';
import ShrimpRoll from '../components/ShrimpRoll';
import Testimonials from '../components/Testimonials';
import Features from '../components/Features';
import Stores from '../components/Stores';
import Footer from '../components/Footer';
import ScrollReveal from '../components/ScrollReveal';
import { API_V1, getImageUrl, fetchApi } from '../config/api';
import { clearOrderSuccessNotice, readOrderSuccessNotice } from '../utils/orderSuccessNotice';
import { refAmount } from '../utils/money';

// Categorías de combos para los menús de inicio
const COMBO_CATEGORY_MENU1 = 'rolls-mixtos';
const COMBO_CATEGORY_MENU2 = 'pollo-crispy';
const COMBOS_PER_PAGE_LIMIT = 15;

// Normaliza un combo del endpoint /combos para usarlo en las product-card del menú
const normalizeComboForMenu = (c) => ({
  id: c.id ?? c.combo_id,
  name: c.name || 'Combo',
  image: getImageUrl(c.image_url ?? c.thumbnail ?? c.thumbnail_url ?? c.image),
  price: refAmount(c.price_eur),
});

const Home = () => {
  const [menu1Products, setMenu1Products] = useState([]);
  const [menu2Products, setMenu2Products] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState(null);
  const [orderNotice, setOrderNotice] = useState(null);

  useEffect(() => {
    const notice = readOrderSuccessNotice();
    if (notice?.message) {
      setOrderNotice(notice);
    }
  }, []);

  const dismissOrderNotice = () => {
    clearOrderSuccessNotice();
    setOrderNotice(null);
  };

  useEffect(() => {
    const fetchCombosForMenus = async () => {
      try {
        setMenuError(null);

        const paramsMenu1 = new URLSearchParams();
        paramsMenu1.set('per_page', String(COMBOS_PER_PAGE_LIMIT));
        paramsMenu1.set('category', COMBO_CATEGORY_MENU1);

        const paramsMenu2 = new URLSearchParams();
        paramsMenu2.set('per_page', String(COMBOS_PER_PAGE_LIMIT));
        paramsMenu2.set('category', COMBO_CATEGORY_MENU2);

        const [res1, res2] = await Promise.all([
          fetchApi(`${API_V1}/combos?${paramsMenu1.toString()}`),
          fetchApi(`${API_V1}/combos?${paramsMenu2.toString()}`),
        ]);
        if (!res1.ok) throw new Error('Error al cargar combos del menú 1');
        if (!res2.ok) throw new Error('Error al cargar combos del menú 2');
        const json1 = await res1.json();
        const json2 = await res2.json();
        const list1 = json1.data ?? json1;
        const list2 = json2.data ?? json2;
        const raw1 = Array.isArray(list1) ? list1 : (Array.isArray(list1?.data) ? list1.data : []);
        const raw2 = Array.isArray(list2) ? list2 : (Array.isArray(list2?.data) ? list2.data : []);
        setMenu1Products(raw1.map(normalizeComboForMenu));
        setMenu2Products(raw2.map(normalizeComboForMenu));
      } catch (err) {
        setMenuError(err.message);
        setMenu1Products([]);
        setMenu2Products([]);
      } finally {
        setMenuLoading(false);
      }
    };
    fetchCombosForMenus();
  }, []);

  return (
    <div className="App">
      <Header />
      {orderNotice && (
        <div className="order-success-banner" role="status">
          <p className="order-success-banner-text">{orderNotice.message}</p>
          <button
            type="button"
            className="order-success-banner-close"
            onClick={dismissOrderNotice}
            aria-label="Cerrar aviso"
          >
            ×
          </button>
        </div>
      )}
      <main>
        <ScrollReveal as="section" once>
          <Hero />
        </ScrollReveal>

        <ScrollReveal as="section" once delayMs={50}>
          <Menu
            title="Sushi"
            products={menu1Products}
            loading={menuLoading}
            error={menuError}
          />
        </ScrollReveal>

        <ScrollReveal as="section" once delayMs={80}>
          <FeaturedProducts />
        </ScrollReveal>

        <ScrollReveal as="section" once delayMs={110}>
          <BestSellers />
        </ScrollReveal>

        <ScrollReveal as="section" once delayMs={140}>
          <Menu
            title="Pollo Crispy"
            products={menu2Products}
            showPrice={true}
            loading={menuLoading}
            error={menuError}
            largeCards={true}
          />
        </ScrollReveal>

        <ScrollReveal as="section" once delayMs={170}>
          <ShrimpRoll />
        </ScrollReveal>

        <ScrollReveal as="section" once delayMs={200}>
          <Testimonials />
        </ScrollReveal>

        <ScrollReveal as="section" once delayMs={230}>
          <Features />
        </ScrollReveal>

        <ScrollReveal as="section" once delayMs={260}>
          <Stores />
        </ScrollReveal>
      </main>
      <Footer />
    </div>
  );
};

export default Home;

